import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChartStyle, CompleteKundali, KundaliChartData, PlanetPosition, Language, AshtakvargaData } from '../types/kundali';
import { ZODIAC_SIGNS, getSavBySign, getSavByBhava } from '../utils/vedicEngine';
import { getPlanetShortCode, getZodiacName, UI_TEXT } from '../utils/language';
import { resolveChartLayout, NORTH_INDIAN_HOUSE_POLYGONS } from '../utils/chartLayoutEngine';
import { formatDOB, formatTimeToAMPM } from '../utils/dateUtils';
import { Compass, Sparkles, Info, LayoutGrid, Grid, Maximize2, Minimize2, GripVertical, RotateCcw, Sun, Moon, ChevronDown, ChevronUp, Zap, Target, Activity, Award, Shield, List, Calendar, Gem, Clock, ArrowUpRight, BarChart3, Triangle, Dna, FileText, ArrowLeftRight, Columns, Rows, Layers, User, MapPin, SlidersHorizontal, Printer } from 'lucide-react';
import { PublicationDossierView } from './PublicationDossierView';
import { GocharView } from './GocharView';
import { DashaTimeline } from './DashaTimeline';
import { AshtakvargaView } from './AshtakvargaView';
import { ShadbalaView } from './ShadbalaView';
import { NavtaraView } from './NavtaraView';
import { HouseBreakdownView } from './HouseBreakdownView';
import { HouseInspectorView } from './HouseInspectorView';
import { MuhurtaAdvisor } from './MuhurtaAdvisor';
import { KarkaView } from './KarkaView';
import { RemediesView } from './RemediesView';
import { UnifiedRemedialEngineView } from './UnifiedRemedialEngineView';
import { AstrologerDiagnosticDossier } from './AstrologerDiagnosticDossier';
import { DualViewLayout } from './DualViewLayout';
import { ResponsiveChartContainer } from './ResponsiveChartContainer';
import { UserProfile } from '../lib/firebase';
import { CustomRemediesManager } from './CustomRemediesManager';
import { UnifiedPredictiveCockpit } from './UnifiedPredictiveCockpit';
import { DynamicMultiViewCanvas } from './DynamicMultiViewCanvas';
import { PanchangMuhurtaView } from './PanchangMuhurtaView';
import { FullPanchangView } from './FullPanchangView';

interface ChartViewerProps {
  kundali: CompleteKundali;
  chartStyle: ChartStyle;
  setChartStyle: (style: ChartStyle) => void;
  lang?: Language;
  setLang?: (l: Language) => void;
  currentUser?: UserProfile | null;
  onEditProfile?: () => void;
  initialDivisionalCode?: string;
}

export const ChartViewer: React.FC<ChartViewerProps> = ({
  kundali,
  chartStyle,
  setChartStyle,
  lang = 'en',
  setLang,
  currentUser,
  onEditProfile,
  initialDivisionalCode,
}) => {
  const [viewMode, setViewMode] = useState<'single' | 'dual' | 'quad' | 'canvas'>('single');
  const [selectedDivisionalCode, setSelectedDivisionalCode] = useState<string>(initialDivisionalCode || 'D1');
  const [selectedHouseNumber, setSelectedHouseNumber] = useState<number>(1);
  const [selectedPlanet, setSelectedPlanet] = useState<PlanetPosition | null>(null);
  const [showDegrees, setShowDegrees] = useState<boolean>(() => {
    try {
      return localStorage.getItem('astro_degrees_pref') !== 'false';
    } catch {
      return true;
    }
  });
  const [antiOverlap, setAntiOverlap] = useState<boolean>(() => {
    try {
      return localStorage.getItem('astro_anti_overlap_pref') !== 'false';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    const handleStorageChange = () => {
      try {
        setShowDegrees(localStorage.getItem('astro_degrees_pref') !== 'false');
        setAntiOverlap(localStorage.getItem('astro_anti_overlap_pref') !== 'false');
      } catch {}
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('astro_degrees_pref_changed', handleStorageChange);
    window.addEventListener('astro_anti_overlap_pref_changed', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('astro_degrees_pref_changed', handleStorageChange);
      window.removeEventListener('astro_anti_overlap_pref_changed', handleStorageChange);
    };
  }, []);
  
  // Customization state for each of the 4 quadrants (index 0-3)
  const [quadSelections, setQuadSelections] = useState<string[]>(['D1', 'Chandra', 'Surya', 'D9']);
  const [expandedSelection, setExpandedSelection] = useState<string | null>(null);
  const [maxChartDim, setMaxChartDim] = useState<number>(1200);
  const [showStelliumTable, setShowStelliumTable] = useState<boolean>(false);
  const [isLegendExpanded, setIsLegendExpanded] = useState<boolean>(false);

  // 3 Layout Modes: 'dock' (Split Side Dock), 'wrap' (Adaptive Full-Width Flow), 'top' (Sticky Top Hero)
  const [layoutMode, setLayoutMode] = useState<'wrap' | 'top' | 'dock'>(() => {
    try {
      const saved = localStorage.getItem('astro_chart_layout_mode');
      if (saved === 'wrap' || saved === 'top' || saved === 'dock') return saved;
    } catch {}
    return 'dock';
  });

  const handleLayoutModeChange = (mode: 'wrap' | 'top' | 'dock') => {
    setLayoutMode(mode);
    try {
      localStorage.setItem('astro_chart_layout_mode', mode);
    } catch {}
  };

  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1024);
  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  // Maximize Mode: Expands chart to 100% full-width theater mode
  const [isMaximized, setIsMaximized] = useState<boolean>(false);

  // Dynamic Left/Right Split Percentage (starting from 25% with 5% increments, default 50%)
  const [splitPercent, setSplitPercent] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('astro_workspace_split_percent');
      if (saved) {
        const val = Number(saved);
        if (!isNaN(val) && val >= 25 && val <= 75) return Math.round(val / 5) * 5;
      }
    } catch {}
    return 50;
  });

  const [isDraggingSplit, setIsDraggingSplit] = useState<boolean>(false);
  const [showRatioPopover, setShowRatioPopover] = useState<boolean>(false);
  const splitContainerRef = useRef<HTMLDivElement | null>(null);
  const ratioPopoverRef = useRef<HTMLDivElement | null>(null);
  const leftPanelRef = useRef<HTMLDivElement | null>(null);
  const rightPanelRef = useRef<HTMLDivElement | null>(null);

  // Close Ratio Popover on Outside Click
  useEffect(() => {
    if (!showRatioPopover) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ratioPopoverRef.current && !ratioPopoverRef.current.contains(e.target as Node)) {
        setShowRatioPopover(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showRatioPopover]);

  const handleResetScroll = () => {
    // 1. Scroll window and document roots
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
    document.body.scrollTo({ top: 0, behavior: 'smooth' });

    // 2. Scroll dedicated panel refs
    if (leftPanelRef.current) leftPanelRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    if (rightPanelRef.current) rightPanelRef.current.scrollTo({ top: 0, behavior: 'smooth' });

    // 3. Scroll all internal custom scrollbars and layout containers
    const scrollableElements = document.querySelectorAll('.custom-scrollbar, main, #root');
    scrollableElements.forEach((el) => {
      try {
        el.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {}
    });
  };

  const handleSetSplitPercent = (pct: number) => {
    const stepped = Math.round(pct / 5) * 5;
    const clamped = Math.max(25, Math.min(75, stepped));
    setSplitPercent(clamped);
    try {
      localStorage.setItem('astro_workspace_split_percent', String(clamped));
    } catch {}
  };

  const handleResetSplit = () => {
    handleSetSplitPercent(50);
  };

  // Mouse & Touch Drag Handlers for Resizer
  useEffect(() => {
    if (!isDraggingSplit) return;

    const handleMove = (clientX: number) => {
      if (!splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      if (rect.width <= 0) return;
      const newPercent = ((clientX - rect.left) / rect.width) * 100;
      handleSetSplitPercent(newPercent);
    };

    const onMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      handleMove(e.clientX);
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        handleMove(e.touches[0].clientX);
      }
    };

    const onEnd = () => {
      setIsDraggingSplit(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);

    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [isDraggingSplit]);

  // Astrologer Keyboard Shortcuts (M: Maximize, Esc: Exit Maximize, [: -5%, ]: +5%, \: 50:50)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept if user is typing in an input or textarea
      const target = e.target as HTMLElement;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }

      if (e.key === 'm' || e.key === 'M') {
        setIsMaximized((prev) => !prev);
      } else if (e.key === 'Escape' && isMaximized) {
        setIsMaximized(false);
      } else if (e.key === '[') {
        handleSetSplitPercent(splitPercent - 5);
      } else if (e.key === ']') {
        handleSetSplitPercent(splitPercent + 5);
      } else if (e.key === '\\') {
        handleResetSplit(); // Balanced 50:50
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMaximized, splitPercent]);

  const activeLang: Language = (lang as Language) || 'en';
  const ui = UI_TEXT[activeLang];

  const divisionalList = [
    { code: 'D1', label: ui.D1 },
    { code: 'Chandra', label: ui.Chandra },
    { code: 'Surya', label: ui.Surya },
    { code: 'Transit', label: (ui as any).Transit || 'Transit (Gochar Chart)' },
    { code: 'D2', label: ui.D2 },
    { code: 'D3', label: ui.D3 },
    { code: 'D4', label: ui.D4 },
    { code: 'D7', label: ui.D7 },
    { code: 'D9', label: ui.D9 },
    { code: 'D10', label: ui.D10 },
    { code: 'D12', label: ui.D12 },
    { code: 'D16', label: ui.D16 },
    { code: 'D20', label: ui.D20 },
    { code: 'D24', label: ui.D24 },
    { code: 'D27', label: ui.D27 },
    { code: 'D30', label: ui.D30 },
    { code: 'D40', label: ui.D40 },
    { code: 'D45', label: ui.D45 },
    { code: 'D60', label: ui.D60 }
  ];

  const specialViews = [
    { code: 'PublicationDossier', label: activeLang === 'hi' ? 'प्रकाशन डॉसियर' : 'Publication Dossier', icon: <Printer className="w-4 h-4 text-astro-primary" /> },
    { code: 'ShubhaEssentials', label: activeLang === 'hi' ? 'शुभ तत्व व उपाय' : 'Shubha Essentials & Remedies', icon: <Sparkles className="w-4 h-4 text-[#B58A4A]" /> },
    { code: 'Ashtakvarga', label: activeLang === 'hi' ? 'अष्टकवर्ग' : 'Ashtakvarga', icon: <LayoutGrid className="w-4 h-4" /> },
    { code: 'Dasha', label: activeLang === 'hi' ? 'विंशोत्तरी दशा' : 'Vimshottari Dasha', icon: <Clock className="w-4 h-4" /> },
    { code: 'Navtara', label: activeLang === 'hi' ? 'नवतारा' : 'Navtara', icon: <Sparkles className="w-4 h-4" /> },
    { code: 'Gochar', label: activeLang === 'hi' ? 'वर्तमान गोचर' : 'Current Gochar', icon: <Sun className="w-4 h-4" /> },
    { code: 'Muhurta', label: activeLang === 'hi' ? 'मुहूर्त' : 'Muhurta Advisor', icon: <Calendar className="w-4 h-4" /> },
    { code: 'FullPanchang', label: activeLang === 'hi' ? 'सम्पूर्ण पंचांग' : 'Full Panchang', icon: <Calendar className="w-4 h-4 text-[#B58A4A]" /> },
    { code: 'Karka', label: activeLang === 'hi' ? 'कारक' : 'Significators (Karka)', icon: <Target className="w-4 h-4" /> },
    { code: 'HouseStrength', label: activeLang === 'hi' ? 'भाव बल' : 'House Strength', icon: <Shield className="w-4 h-4" /> },
    { code: 'Shadbala', label: activeLang === 'hi' ? 'षडबल' : 'Shadbala', icon: <BarChart3 className="w-4 h-4" /> },
    { code: 'AstrologerDossier', label: activeLang === 'hi' ? 'ज्योतिषी डॉसियर' : "Astrologer's Dossier", icon: <Compass className="w-4 h-4 text-amber-500" /> }
  ];

  const singleViewTabs = [
    ...divisionalList,
    ...specialViews
  ];

  const quadOptions = [
    ...divisionalList,
    { code: 'Ashtakvarga', label: 'Ashtakvarga' },
    { code: 'Dasha', label: 'Vimshottari' },
    { code: 'Navtara', label: 'Navtara' },
    { code: 'Muhurta', label: 'Muhurta Advisor' },
    { code: 'FullPanchang', label: activeLang === 'hi' ? 'सम्पूर्ण पंचांग' : 'Full Panchang' },
    { code: 'Gochar', label: 'Current Gochar' },
    { code: 'Karka', label: 'Karka (Significators)' },
    { code: 'Remedies', label: activeLang === 'hi' ? 'वैदिक उपाय' : 'Vedic Remedies' },
    { code: 'AstrologerDossier', label: "Astrologer's Dossier" }
  ];

  const currentChartData: KundaliChartData = (kundali.charts as any)[selectedDivisionalCode] || kundali.charts.D1;

  // Sync initialDivisionalCode changes
  useEffect(() => {
    if (initialDivisionalCode) {
      setSelectedDivisionalCode(initialDivisionalCode);
      setViewMode('single');
      setExpandedSelection(null);
    }
  }, [initialDivisionalCode]);

  // House occupants computation
  const houseOccupants = React.useMemo(() => {
    const map: Record<number, PlanetPosition[]> = {};
    for (let i = 1; i <= 12; i++) map[i] = [];
    currentChartData?.planets?.forEach(p => {
      if (p.house && p.house >= 1 && p.house <= 12) {
        map[p.house].push(p);
      }
    });
    return map;
  }, [currentChartData]);

  const heavyConjunctions = React.useMemo(() => {
    return Object.entries(houseOccupants)
      .filter(([h, planets]) => planets.length >= 3)
      .map(([h, planets]) => ({ house: Number(h), planets }));
  }, [houseOccupants]);

  // Global tab navigation listener for Gemstones and Full Panchang
  useEffect(() => {
    const handleNav = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      if (detail === 'shubha-essentials' || detail === 'ShubhaEssentials' || detail === 'gemstones' || detail === 'remedies') {
        setSelectedDivisionalCode('ShubhaEssentials');
        setViewMode('single');
        setExpandedSelection(null);
      } else if (detail === 'full-panchang' || detail === 'FullPanchang') {
        setSelectedDivisionalCode('FullPanchang');
        setViewMode('single');
        setExpandedSelection(null);
      }
    };
    window.addEventListener('astro_navigate_tab', handleNav);
    return () => window.removeEventListener('astro_navigate_tab', handleNav);
  }, []);

  const scrollToHouseInspector = () => {
    if (rightPanelRef.current) {
      const inspectorEl = rightPanelRef.current.querySelector('#house-inspector-section');
      if (inspectorEl) {
        inspectorEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const handleSelectPlanet = (p: PlanetPosition | null) => {
    setSelectedPlanet(p);
    if (p && p.house) {
      setSelectedHouseNumber(p.house);
      if (isDesktop && (layoutMode === 'dock' || isMaximized)) {
        setTimeout(() => {
          scrollToHouseInspector();
        }, 50);
      }
    }
  };

  const handleSelectHouse = (hNum: number) => {
    setSelectedHouseNumber(hNum);
    if (isDesktop && (layoutMode === 'dock' || isMaximized)) {
      setTimeout(() => {
        scrollToHouseInspector();
      }, 50);
    }
  };

  return (
    <div className={isMaximized ? "fixed inset-0 z-[9999] bg-[#F6F4EE] flex flex-col p-2 sm:p-3 overflow-hidden" : "space-y-6"}>

      {/* Top Toolbar / Header: In Studio Mode, show a dedicated clean studio header; otherwise show regular view modes & sub-nav */}
      {isMaximized ? (
        <div className="w-full shrink-0 flex items-center justify-between px-3 py-2 bg-[#FDFCF9] border border-[#E2DED5] rounded-xl shadow-xs mb-2">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-astro-primary text-white flex items-center justify-center font-bold shadow-2xs">
              <Compass className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-sm sm:text-base font-bold text-[#263633] font-serif">
                  {currentChartData.title}
                </h2>
                <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-astro-teal-tint text-astro-secondary border border-astro-secondary/20 font-bold">
                  {selectedDivisionalCode}
                </span>
                <span className="hidden sm:inline-flex text-[11px] font-semibold text-astro-primary bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md">
                  {activeLang === 'hi' ? 'स्टूडियो समानांतर वाचन (Studio Mode)' : 'Studio Parallel Reading'}
                </span>
              </div>
              <p className="text-[11px] text-[#52635F]">
                {activeLang === 'hi' ? 'लग्न:' : 'Ascendant / Lagna:'} <span className="font-semibold text-[#263633]">{getZodiacName(ZODIAC_SIGNS[currentChartData?.ascendantIndex ?? 0], activeLang)}</span> ({((currentChartData?.ascendantIndex ?? 0) + 1)}) • {activeLang === 'hi' ? 'बायां पैनल स्थिर, दायां पैनल स्क्रॉल' : 'Left chart fixed, right cockpit scrollable'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Reset Both Panels Scroll */}
            <button
              type="button"
              onClick={handleResetScroll}
              className="px-2.5 py-1 rounded-lg text-xs font-semibold text-astro-secondary hover:text-astro-primary bg-[#FAF6ED] hover:bg-[#F2ECE0] border border-astro-border flex items-center gap-1.5 transition-all shadow-2xs cursor-pointer"
              title={activeLang === 'hi' ? 'दोनों पैनलों को ऊपर स्क्रॉल करें' : 'Scroll both panels to top'}
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{activeLang === 'hi' ? 'शीर्ष पर जाएं' : 'Reset Scroll'}</span>
            </button>

            {/* Split Ratio Controls in Studio Mode */}
            {isDesktop && (
              <div className="hidden md:flex items-center gap-1.5 bg-[#FAF6ED] px-2.5 py-1 rounded-lg border border-astro-border shadow-2xs">
                <span className="text-[11px] font-semibold text-astro-secondary select-none">
                  {activeLang === 'hi' ? 'विभाजन:' : 'Split:'}
                </span>
                <input
                  type="range"
                  min={30}
                  max={70}
                  step={5}
                  value={splitPercent}
                  onChange={(e) => handleSetSplitPercent(Number(e.target.value))}
                  className="w-20 h-1.5 bg-[#D5CEBF] rounded-lg appearance-none cursor-pointer accent-[#174C4B] focus:outline-none"
                  title={`${splitPercent}% / ${100 - splitPercent}%`}
                />
                <span className="text-[11px] font-mono font-bold text-astro-primary min-w-[32px] text-right select-none">
                  {splitPercent}%
                </span>
                <button
                  type="button"
                  onClick={handleResetSplit}
                  className="ml-1 text-[10px] font-semibold text-astro-secondary hover:text-astro-primary px-1.5 py-0.5 rounded bg-white border border-astro-border"
                  title="50:50 Reset"
                >
                  50:50
                </button>
              </div>
            )}

            {/* Divisional Chart Quick Switcher in Studio Mode */}
            <div className="relative">
              <select
                value={selectedDivisionalCode}
                onChange={(e) => {
                  if (e.target.value) {
                    setSelectedDivisionalCode(e.target.value);
                  }
                }}
                className="appearance-none bg-astro-teal-tint border border-astro-secondary/30 text-astro-secondary font-bold text-xs rounded-lg pl-3 pr-7 py-1.5 shadow-2xs cursor-pointer focus:outline-none"
              >
                {divisionalList.map((d) => (
                  <option key={d.code} value={d.code}>
                    {d.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-3.5 h-3.5 text-astro-secondary absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>

            {/* Exit Studio Mode / Restore Button */}
            <button
              type="button"
              onClick={() => setIsMaximized(false)}
              className="px-3 py-1.5 rounded-lg font-bold text-xs bg-astro-primary text-white hover:bg-astro-primary/90 flex items-center gap-1.5 shadow-xs cursor-pointer transition-all"
              title={activeLang === 'hi' ? 'सामान्य दृश्य पर वापस लौटें (Esc)' : 'Restore normal view (Esc)'}
            >
              <Minimize2 className="w-3.5 h-3.5" />
              <span>{activeLang === 'hi' ? 'सामान्य दृश्य (Esc)' : 'Restore (Esc)'}</span>
            </button>
          </div>
        </div>
      ) : (
        /* Consolidated Primary Toolbar: View Modes, Rashi Selector & Analytical Sub-Nav Strip (Frameless Canvas Float) */
        <div className="w-full flex items-center gap-3 overflow-x-auto custom-horizontal-scrollbar py-2 px-0.5 bg-transparent border-0 shadow-none">
          {/* Multi-View Segmented Control & Rashi Selector Dropdown */}
          <div className="flex items-center gap-2 shrink-0">
            <div className="flex items-center gap-1 bg-[#EFECE6] p-1 rounded-xl border border-[#E2DED5] text-xs shadow-2xs" role="toolbar" aria-label={activeLang === 'hi' ? 'चार्ट दृश्य मोड' : 'Chart View Modes'}>
              <button
                type="button"
                aria-pressed={viewMode === 'single'}
                onClick={() => {
                  setViewMode('single');
                  setSelectedDivisionalCode('D1');
                }}
                className={`px-3 py-1.5 rounded-lg font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                  viewMode === 'single' && divisionalList.some(d => d.code === selectedDivisionalCode)
                    ? 'bg-white text-astro-primary shadow-xs border border-astro-border/80 ring-1 ring-black/5'
                    : 'text-[#52635F] hover:text-[#263633] hover:bg-black/5'
                }`}
              >
                <Compass className="w-3.5 h-3.5 text-astro-secondary" />
                <span>{activeLang === 'hi' ? '१ चार्ट' : '1 Chart'}</span>
              </button>

              <button
                type="button"
                aria-pressed={viewMode === 'dual'}
                onClick={() => setViewMode('dual')}
                className={`px-3 py-1.5 rounded-lg font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                  viewMode === 'dual'
                    ? 'bg-white text-astro-primary shadow-xs border border-astro-border/80 ring-1 ring-black/5'
                    : 'text-[#52635F] hover:text-[#263633] hover:bg-black/5'
                }`}
              >
                <ArrowLeftRight className="w-3.5 h-3.5 text-astro-secondary" />
                <span>{activeLang === 'hi' ? '२ चार्ट' : '2 Chart'}</span>
              </button>

              <button
                type="button"
                aria-pressed={viewMode === 'quad'}
                onClick={() => setViewMode('quad')}
                className={`px-3 py-1.5 rounded-lg font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                  viewMode === 'quad'
                    ? 'bg-white text-astro-primary shadow-xs border border-astro-border/80 ring-1 ring-black/5'
                    : 'text-[#52635F] hover:text-[#263633] hover:bg-black/5'
                }`}
              >
                <LayoutGrid className="w-3.5 h-3.5 text-astro-secondary" />
                <span>{activeLang === 'hi' ? '४ चार्ट' : '4 Chart'}</span>
              </button>

              <button
                type="button"
                aria-pressed={viewMode === 'canvas'}
                onClick={() => setViewMode('canvas')}
                className={`px-3 py-1.5 rounded-lg font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                  viewMode === 'canvas'
                    ? 'bg-white text-astro-primary shadow-xs border border-astro-border/80 ring-1 ring-black/5'
                    : 'text-[#52635F] hover:text-[#263633] hover:bg-black/5'
                }`}
              >
                <Grid className="w-3.5 h-3.5 text-astro-accent" />
                <span>{activeLang === 'hi' ? 'मल्टी-व्यू ग्रिड' : 'Multi-View Canvas'}</span>
              </button>
            </div>

            {/* Chart / Divisional Selection Dropdown */}
            <div className="relative">
              <select
                value={divisionalList.some(d => d.code === selectedDivisionalCode) ? selectedDivisionalCode : ''}
                onChange={(e) => {
                  if (e.target.value) {
                    setSelectedDivisionalCode(e.target.value);
                    setViewMode('single');
                  }
                }}
                className={`appearance-none border font-bold text-xs rounded-xl pl-3.5 pr-8 py-2 shadow-xs cursor-pointer focus:outline-none focus:ring-2 focus:ring-astro-secondary/20 transition-all max-w-[280px] sm:max-w-md truncate ${
                  divisionalList.some(d => d.code === selectedDivisionalCode)
                    ? 'bg-astro-teal-tint border-astro-secondary/30 text-astro-secondary'
                    : 'bg-astro-card border-[#E2DED5] text-[#52635F]'
                }`}
                title={activeLang === 'hi' ? 'वर्ग कुंडली चुनें' : 'Select Divisional Chart'}
              >
                {!divisionalList.some(d => d.code === selectedDivisionalCode) && (
                  <option value="">{activeLang === 'hi' ? 'वर्ग कुंडली चुनें...' : 'Select Chart...'}</option>
                )}
                {divisionalList.map((d) => (
                  <option key={d.code} value={d.code}>
                    {d.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-astro-secondary absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>

          {/* Analytical Sub-Navigation Strip (Ashtakvarga, Vimshottari Dasha, Navtara, etc.) */}
          <div className="flex items-center gap-2 min-w-max px-0.5">
            {specialViews.map((view) => {
              const isActive = selectedDivisionalCode === view.code;
              return (
                <button
                  key={view.code}
                  onClick={() => {
                    setSelectedDivisionalCode(view.code);
                    setViewMode('single');
                  }}
                  className={`shrink-0 whitespace-nowrap h-7 px-2.5 inline-flex items-center gap-1.5 rounded-xl border text-xs font-medium transition-all shadow-2xs hover:scale-102 select-none cursor-pointer ${
                    isActive
                      ? 'bg-astro-primary text-astro-bg border-astro-primary font-semibold'
                      : 'bg-astro-bg text-astro-text border-astro-border hover:bg-astro-card hover:border-astro-accent'
                  }`}
                >
                  <span className={isActive ? 'text-astro-bg' : 'text-astro-primary'}>{view.icon}</span>
                  <span>{view.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* DYNAMIC MULTI-VIEW CANVAS WORKBENCH GRID */}
      {viewMode === 'canvas' && (
        <DynamicMultiViewCanvas kundali={kundali} lang={lang} />
      )}

      {/* SINGLE CHART VIEW MODE: Full Interactive Stage, Cockpit & House Inspector */}
      {viewMode === 'single' && (
        divisionalList.some(d => d.code === selectedDivisionalCode) ? (
          (() => {
            const renderChartCard = (isSticky = false, maxCustomDim?: number) => (
              <div className="w-full bg-[#FDFCF9] border border-[#E2DED5] rounded-2xl p-3 sm:p-4 shadow-xs flex flex-col space-y-3">
                {/* Chart Header */}
                <div className="w-full shrink-0 flex flex-wrap items-center justify-between gap-2 mb-1 border-b border-[#E2DED5] pb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Compass className="w-5 h-5 text-astro-secondary shrink-0" />
                    <h3 className="text-base sm:text-lg font-bold text-[#263633] font-serif">
                      {currentChartData.title}
                    </h3>
                    {selectedDivisionalCode !== 'D1' && (
                      <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-astro-teal-tint text-astro-secondary border border-astro-secondary/20 font-bold shrink-0">
                        {selectedDivisionalCode}
                      </span>
                    )}
                    {isMaximized && (
                      <span className="text-[10px] font-bold font-mono px-2 py-0.5 rounded-full bg-astro-primary text-white shrink-0 shadow-2xs">
                        {activeLang === 'hi' ? 'समानांतर वाचन' : 'Parallel Read'}
                      </span>
                    )}
                  </div>

                  {/* Right side of Chart Header: Icon-Only Controls with Rich Tooltips & Popovers */}
                  <div className="flex items-center gap-1 sm:gap-1.5 flex-wrap justify-end">
                    {/* Reset Scroll Button */}
                    {!isMaximized && (
                      <button
                        type="button"
                        onClick={handleResetScroll}
                        className="p-1.5 rounded-lg text-astro-secondary hover:text-astro-primary bg-[#FAF6ED] hover:bg-[#F2ECE0] border border-astro-border flex items-center justify-center transition-all shadow-2xs cursor-pointer hover:border-astro-secondary/40"
                        title={activeLang === 'hi' ? 'सभी पैनलों को ऊपर स्क्रॉल करें' : 'Scroll all panels to top'}
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    )}

                    {/* Chart Size & Split Ratio Icon with Hover / Click Popover */}
                    {(layoutMode === 'dock' || layoutMode === 'wrap') && !isMaximized && isDesktop && (
                      <div className="relative" ref={ratioPopoverRef}>
                        <button
                          type="button"
                          onClick={() => setShowRatioPopover(prev => !prev)}
                          className="px-2 py-1.5 rounded-lg text-astro-secondary hover:text-astro-primary bg-[#FAF6ED] hover:bg-[#F2ECE0] border border-astro-border flex items-center gap-1.5 transition-all shadow-2xs cursor-pointer hover:border-astro-secondary/40"
                          title={activeLang === 'hi' ? `चार्ट आकार एवं अनुपात: ${splitPercent}% (क्लिक या होवर करके बदलें)` : `Chart Ratio & Size: ${splitPercent}% (Click to adjust)`}
                        >
                          <SlidersHorizontal className="w-4 h-4" />
                          <span className="text-[11px] font-mono font-bold text-astro-primary select-none">
                            {splitPercent}%
                          </span>
                        </button>

                        {/* Ratio Popover */}
                        {showRatioPopover && (
                          <div className="absolute right-0 top-full mt-2 w-64 z-50 bg-white border border-astro-border rounded-xl p-3 shadow-xl space-y-3 animate-in fade-in zoom-in-95 duration-150">
                            <div className="flex items-center justify-between border-b border-astro-border pb-1.5">
                              <span className="text-xs font-bold text-[#263633]">
                                {activeLang === 'hi' ? 'चार्ट विभाजन अनुपात' : 'Chart Split Ratio'}
                              </span>
                              <span className="text-[11px] font-mono font-bold text-astro-primary bg-astro-teal-tint px-1.5 py-0.5 rounded border border-astro-secondary/20">
                                {splitPercent}% : {100 - splitPercent}%
                              </span>
                            </div>

                            {/* Preset Buttons */}
                            <div className="grid grid-cols-5 gap-1">
                              {[30, 40, 50, 60, 70].map((pct) => (
                                <button
                                  key={pct}
                                  type="button"
                                  onClick={() => handleSetSplitPercent(pct)}
                                  className={`px-1 py-1 text-[10px] font-mono font-bold rounded border transition-colors cursor-pointer text-center ${
                                    splitPercent === pct
                                      ? 'bg-astro-primary text-white border-astro-primary shadow-2xs'
                                      : 'bg-[#FAF6ED] text-astro-secondary hover:bg-[#F2ECE0] border-astro-border'
                                  }`}
                                  title={`${pct}% : ${100 - pct}%`}
                                >
                                  {pct}:{100 - pct}
                                </button>
                              ))}
                            </div>

                            {/* Fine Range Slider */}
                            <div className="space-y-1">
                              <div className="flex justify-between text-[10px] text-astro-secondary-text font-medium">
                                <span>25%</span>
                                <span>50%</span>
                                <span>75%</span>
                              </div>
                              <input
                                type="range"
                                min={25}
                                max={75}
                                step={5}
                                value={splitPercent}
                                onChange={(e) => handleSetSplitPercent(Number(e.target.value))}
                                className="w-full h-1.5 bg-[#D5CEBF] rounded-lg appearance-none cursor-pointer accent-[#174C4B] focus:outline-none"
                              />
                            </div>

                            {/* Footer / Reset */}
                            <div className="flex items-center justify-between pt-1.5 border-t border-astro-border/60">
                              <span className="text-[10px] text-astro-secondary-text">
                                {activeLang === 'hi' ? 'डिफ़ॉल्ट अनुपात' : 'Default Ratio'}
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  handleResetSplit();
                                  setShowRatioPopover(false);
                                }}
                                className="text-[11px] font-bold text-astro-primary hover:text-astro-secondary hover:underline cursor-pointer"
                              >
                                {activeLang === 'hi' ? '५०:५० रीसेट' : 'Reset 50:50'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 3 Layout Modes Toggle (Icon-Only with Rich Tooltips) */}
                    {!isMaximized && (
                      <div className="flex items-center bg-[#FAF6ED] p-0.5 rounded-lg border border-astro-border shadow-2xs">
                        <button
                          type="button"
                          onClick={() => handleLayoutModeChange('dock')}
                          className={`p-1.5 rounded-md text-[11px] font-bold flex items-center justify-center transition-all cursor-pointer ${
                            layoutMode === 'dock'
                              ? 'bg-astro-primary text-white shadow-2xs'
                              : 'text-astro-secondary hover:text-astro-primary'
                          }`}
                          title={activeLang === 'hi' ? 'डॉक मोड (स्थिर साइड चार्ट, स्क्रॉल दायां पैनल)' : 'Dock Mode (Split Side-by-Side)'}
                        >
                          <Columns className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleLayoutModeChange('wrap')}
                          className={`p-1.5 rounded-md text-[11px] font-bold flex items-center justify-center transition-all cursor-pointer ${
                            layoutMode === 'wrap'
                              ? 'bg-astro-primary text-white shadow-2xs'
                              : 'text-astro-secondary hover:text-astro-primary'
                          }`}
                          title={activeLang === 'hi' ? 'रैप मोड (अनुकूलित विस्तार प्रवाह)' : 'Wrap Mode (Adaptive Full Flow)'}
                        >
                          <Layers className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleLayoutModeChange('top')}
                          className={`p-1.5 rounded-md text-[11px] font-bold flex items-center justify-center transition-all cursor-pointer ${
                            layoutMode === 'top'
                              ? 'bg-astro-primary text-white shadow-2xs'
                              : 'text-astro-secondary hover:text-astro-primary'
                          }`}
                          title={activeLang === 'hi' ? 'शीर्ष मोड (पिन किया गया शीर्ष चार्ट)' : 'Top Mode (Hero Top Pinned Chart)'}
                        >
                          <Rows className="w-4 h-4" />
                        </button>
                      </div>
                    )}

                    {/* Dedicated Maximize Studio Mode Button (Icon-Only with Tooltip) */}
                    <button
                      type="button"
                      onClick={() => setIsMaximized((prev) => !prev)}
                      className={`p-1.5 rounded-lg border flex items-center justify-center transition-all cursor-pointer ${
                        isMaximized
                          ? 'bg-astro-primary text-white border-astro-primary shadow-xs'
                          : 'bg-[#FAF6ED] text-astro-secondary hover:text-astro-primary border-astro-border hover:bg-astro-card hover:border-astro-secondary/40'
                      }`}
                      title={
                        isMaximized
                          ? (activeLang === 'hi' ? 'सामान्य दृश्य पर वापस लौटें (Esc या M)' : 'Restore Standard View (Esc or M)')
                          : (activeLang === 'hi' ? 'स्टूडियो समानांतर वाचन मोड (M)' : 'Maximize Studio Parallel View (M)')
                      }
                    >
                      {isMaximized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Aspect-Ratio Locked Chart Container */}
                <div className={`w-full ${isSticky ? 'max-h-[380px]' : (isMaximized ? 'flex-1 min-h-0 max-h-[calc(100vh-230px)]' : 'max-h-[min(720px,calc(100vh-180px))]')} aspect-square flex items-center justify-center mx-auto py-1`}>
                  <ResponsiveChartContainer maxDimension={maxCustomDim || (isMaximized ? 850 : 720)} className="w-full h-full flex items-center justify-center">
                    {() => (
                      <>
                        {chartStyle === 'north' && (
                          <NorthIndianSvgChart
                            chartData={currentChartData}
                            planets={currentChartData.planets}
                            ashtakvarga={kundali.ashtakvarga}
                            selectedHouseNumber={selectedHouseNumber}
                            onSelectHouse={handleSelectHouse}
                            onSelectPlanet={handleSelectPlanet}
                            lang={lang}
                            showDegrees={showDegrees}
                            antiOverlap={antiOverlap}
                          />
                        )}
                        {chartStyle === 'south' && (
                          <SouthIndianSvgChart
                            chartData={currentChartData}
                            planets={currentChartData.planets}
                            ashtakvarga={kundali.ashtakvarga}
                            selectedHouseNumber={selectedHouseNumber}
                            onSelectHouse={handleSelectHouse}
                            onSelectPlanet={handleSelectPlanet}
                            lang={lang}
                            showDegrees={showDegrees}
                            antiOverlap={antiOverlap}
                          />
                        )}
                        {chartStyle === 'east' && (
                          <EastIndianSvgChart
                            chartData={currentChartData}
                            planets={currentChartData.planets}
                            ashtakvarga={kundali.ashtakvarga}
                            selectedHouseNumber={selectedHouseNumber}
                            onSelectHouse={handleSelectHouse}
                            onSelectPlanet={handleSelectPlanet}
                            lang={lang}
                            showDegrees={showDegrees}
                            antiOverlap={antiOverlap}
                          />
                        )}
                      </>
                    )}
                  </ResponsiveChartContainer>
                </div>

                {/* Bottom Section: If Maximized, show sleek single-line restore bar; Else show Contextual Strip + Legend + Controls */}
                {isMaximized ? (
                  <div className="w-full flex items-center justify-between pt-2 border-t border-astro-border/60 mt-1 text-xs">
                    <span className="text-[11px] text-[#52635F]">
                      {activeLang === 'hi' ? 'बाएं पैनल में पूर्ण विस्तार' : 'Expanded in left panel'} • {currentChartData.title} ({selectedDivisionalCode})
                    </span>
                    <button
                      type="button"
                      onClick={() => setIsMaximized(false)}
                      className="text-[11px] font-bold text-astro-primary hover:text-astro-secondary flex items-center gap-1 cursor-pointer bg-white px-2 py-0.5 rounded border border-astro-border shadow-2xs"
                    >
                      <Minimize2 className="w-3 h-3" />
                      <span>{activeLang === 'hi' ? 'संक्षिप्त करें (Esc)' : 'Restore (Esc)'}</span>
                    </button>
                  </div>
                ) : (
                  <div className="w-full shrink-0 flex flex-col gap-1.5 pt-2 border-t border-astro-border/60 mt-1">
                    {/* Collapsible Chart Legend & Helper Strip */}
                    <div className="w-full pt-1">
                      <button
                        type="button"
                        onClick={() => setIsLegendExpanded(!isLegendExpanded)}
                        className="w-full py-1 px-2 flex items-center justify-between text-[11px] font-semibold text-astro-secondary hover:text-astro-primary bg-[#FAF6ED]/70 hover:bg-[#FAF6ED] rounded-lg border border-astro-border/60 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-1.5">
                          <Info className="w-3.5 h-3.5 text-astro-secondary shrink-0" />
                          <span>{activeLang === 'hi' ? 'संकेत एवं प्रतीक विवरण (Chart Legend & Guide)' : 'Chart Legend & Indicators'}</span>
                        </div>
                        <div className="flex items-center gap-1 text-[10px] text-astro-secondary/80">
                          <span>{isLegendExpanded ? (activeLang === 'hi' ? 'छिपाएं' : 'Hide') : (activeLang === 'hi' ? 'दिखाएं' : 'Show')}</span>
                          {isLegendExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        </div>
                      </button>

                      {isLegendExpanded && (
                        <div className="w-full space-y-2 mt-1.5 p-2 bg-[#FAF6ED]/40 rounded-xl border border-astro-border/50">
                          <ChartLegend lang={activeLang} />
                          <p className="text-[11px] text-[#52635F] text-center flex items-center justify-center gap-1.5 leading-tight pt-1">
                            <Info className="w-3.5 h-3.5 text-astro-secondary shrink-0" />
                            <span>{activeLang === 'hi' ? 'भाव स्थिति, शुभ/अशुभ प्रभाव एवं भावेश विश्लेषण के लिए किसी भी भाव या ग्रह पर क्लिक करें' : 'Click on any house region or planet to inspect condition, benefic/malefic state & aspects'}</span>
                          </p>
                        </div>
                      )}
                    </div>

                    {/* House Conjunction & Stellium Table Toggle & Container */}
                    <div className="w-full pt-0.5 flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => setShowStelliumTable(!showStelliumTable)}
                        className="text-xs font-bold text-astro-primary flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-astro-teal-tint hover:bg-astro-teal-tint/80 transition-colors cursor-pointer"
                      >
                        <List className="w-3.5 h-3.5" />
                        <span>{showStelliumTable ? (activeLang === 'hi' ? 'सारणी छुपाएं (Hide Conjunction Table)' : 'Hide Stellium Table') : (activeLang === 'hi' ? `सभी ग्रहों की भाव सारणी देखें (${heavyConjunctions.length > 0 ? `⚠️ ${heavyConjunctions.length} भारी युति` : 'Inspect All Houses'})` : `View House Conjunction Table (${heavyConjunctions.length > 0 ? `⚠️ ${heavyConjunctions.length} Stelliums` : 'All Houses'})`)}</span>
                      </button>
                      {heavyConjunctions.length > 0 && (
                        <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200">
                          {activeLang === 'hi' ? 'सघन ग्रह युति उपस्थित' : 'High Density Stellium'}
                        </span>
                      )}
                    </div>

                    {showStelliumTable && (
                      <div className="w-full mt-2 p-3 bg-white border border-astro-border rounded-xl shadow-xs space-y-3">
                        <div className="flex items-center justify-between border-b border-astro-border pb-2">
                          <h4 className="text-xs font-bold text-[#263633] font-serif flex items-center gap-1.5">
                            <Columns className="w-3.5 h-3.5 text-astro-secondary" />
                            <span>{activeLang === 'hi' ? 'कुंडली भाव एवं ग्रह युति सारणी (House Conjunction & Stellium Table)' : 'Chart House Occupants & Stellium Table'}</span>
                          </h4>
                          <span className="text-[10px] font-mono text-astro-secondary">{selectedDivisionalCode}</span>
                        </div>
                        <div className="max-h-56 overflow-y-auto space-y-2 pr-1">
                          {Object.entries(houseOccupants).map(([hStr, planets]) => {
                            const hNum = Number(hStr);
                            const signIdx = ((currentChartData.ascendantIndex + hNum - 1) % 12);
                            const zodiacName = getZodiacName(ZODIAC_SIGNS[signIdx], activeLang);
                            return (
                              <div key={hNum} className={`p-2.5 rounded-lg border text-xs flex flex-col sm:flex-row sm:items-center justify-between gap-2 ${planets.length >= 3 ? 'bg-amber-50/60 border-amber-200' : 'bg-[#FAF6ED]/50 border-astro-border'}`}>
                                <div className="flex items-center gap-2">
                                  <span className="w-6 h-6 rounded-full bg-astro-primary text-white flex items-center justify-center font-bold text-[11px]">
                                    {hNum}
                                  </span>
                                  <div>
                                    <span className="font-bold text-[#263633]">
                                      {activeLang === 'hi' ? `भाव ${hNum}` : `House ${hNum}`}
                                    </span>
                                    <span className="text-[11px] text-astro-secondary-text ml-1.5">
                                      ({zodiacName})
                                    </span>
                                  </div>
                                </div>
                                <div className="flex flex-wrap items-center gap-1.5">
                                  {planets.length === 0 ? (
                                    <span className="text-[11px] text-gray-400 italic">
                                      {activeLang === 'hi' ? '(रिक्त भाव / Empty)' : '(Empty House)'}
                                    </span>
                                  ) : (
                                    planets.map((p, idx) => (
                                      <span key={idx} className={`px-2 py-0.5 rounded-md font-mono text-[11px] font-bold border flex items-center gap-1 ${p.dignity === 'Exalted' ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : p.dignity === 'Debilitated' ? 'bg-rose-100 text-rose-800 border-rose-300' : 'bg-white text-[#263633] border-astro-border'}`}>
                                        <span>{getPlanetShortCode(p.name, activeLang)}</span>
                                        <span className="text-[10px] font-normal text-astro-secondary-text">
                                          {Math.floor(p.degreeInSign ?? 0)}°{p.isRetrograde ? '(r)' : ''}
                                        </span>
                                      </span>
                                    ))
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );

            // When isMaximized is true, enter dedicated Studio Parallel Reading layout (Option A - Dual Independent Scroll)
            if (isMaximized) {
              return (
                <div
                  ref={splitContainerRef}
                  className="w-full flex-1 min-h-0 flex flex-col lg:flex-row items-stretch gap-0 lg:gap-2 relative overflow-hidden"
                >
                  {/* Left Column: Independently scrollable left pane with Chart Stage, House Conjunctions & Dignities */}
                  <div
                    ref={leftPanelRef}
                    className="w-full h-full min-w-0 overflow-y-auto pr-1.5 custom-scrollbar shrink-0"
                    style={{ width: isDesktop ? `${splitPercent}%` : '100%' }}
                  >
                    {renderChartCard(false)}
                  </div>

                  {/* Desktop Interactive Drag Divider in Studio Mode */}
                  {isDesktop && (
                    <div
                      role="separator"
                      tabIndex={0}
                      aria-orientation="vertical"
                      onMouseDown={() => setIsDraggingSplit(true)}
                      onTouchStart={() => setIsDraggingSplit(true)}
                      onDoubleClick={handleResetSplit}
                      className="flex items-center justify-center w-3 self-stretch cursor-col-resize group select-none shrink-0 transition-all z-10 -mx-0.5 hover:bg-black/5 rounded-full"
                      title={activeLang === 'hi' ? 'खींचकर आकार बदलें (डबल-क्लिक: ५०:५०)' : 'Drag to resize panels (Double-click: 50:50 reset)'}
                    >
                      <div className={`w-1.5 h-20 rounded-full flex flex-col items-center justify-center transition-all ${
                        isDraggingSplit ? 'bg-astro-primary scale-110 shadow-xs' : 'bg-[#D5CEBF] group-hover:bg-astro-primary/80 group-hover:scale-105'
                      }`}>
                        <div className="w-0.5 h-0.5 bg-white rounded-full my-0.5" />
                        <div className="w-0.5 h-0.5 bg-white rounded-full my-0.5" />
                        <div className="w-0.5 h-0.5 bg-white rounded-full my-0.5" />
                      </div>
                    </div>
                  )}

                  {/* Right Column: Independently Scrollable Cockpit & Full House Inspector */}
                  <div
                    ref={rightPanelRef}
                    className="w-full h-full min-w-0 overflow-y-auto pr-1.5 custom-scrollbar shrink-0"
                    style={{ width: isDesktop ? `calc(${100 - splitPercent}% - 12px)` : '100%' }}
                  >
                    <UnifiedPredictiveCockpit
                      kundali={kundali}
                      chartData={currentChartData}
                      selectedHouseNumber={selectedHouseNumber}
                      onSelectHouse={handleSelectHouse}
                      selectedPlanet={selectedPlanet}
                      onSelectPlanet={handleSelectPlanet}
                      lang={lang}
                      layoutMode="dock"
                      showFullHouseInspector={true}
                      splitPercent={splitPercent}
                    />
                  </div>
                </div>
              );
            }

            // Normal Mode: Render according to selected layoutMode (Approach 1: Viewport-Locked Dual Panes)
            if (layoutMode === 'dock') {
              return (
                <div
                  ref={splitContainerRef}
                  className="w-full flex flex-col lg:flex-row items-stretch gap-0 lg:gap-2 relative"
                >
                  {/* Left Column: Viewport-Locked Independently Scrollable Chart & Data Deck */}
                  <div
                    ref={leftPanelRef}
                    className="w-full min-w-0 lg:h-[calc(100vh-140px)] lg:max-h-[calc(100vh-140px)] lg:overflow-y-auto pr-1.5 custom-scrollbar shrink-0"
                    style={{ width: isDesktop ? `${splitPercent}%` : '100%' }}
                  >
                    {renderChartCard(false)}
                  </div>

                  {/* Desktop Drag Divider */}
                  {isDesktop && (
                    <div
                      role="separator"
                      tabIndex={0}
                      aria-orientation="vertical"
                      onMouseDown={() => setIsDraggingSplit(true)}
                      onTouchStart={() => setIsDraggingSplit(true)}
                      onDoubleClick={handleResetSplit}
                      className="flex items-center justify-center w-3.5 self-stretch cursor-col-resize group select-none py-6 shrink-0 transition-all z-10 -mx-1"
                      title={activeLang === 'hi' ? 'खींचकर आकार बदलें (डबल-क्लिक: ५०:५० रीसेट)' : 'Drag to resize panels (Double-click: 50:50 reset)'}
                    >
                      <div className={`w-1.5 h-16 rounded-full flex flex-col items-center justify-center transition-all ${
                        isDraggingSplit ? 'bg-astro-primary scale-110 shadow-xs' : 'bg-[#D5CEBF] group-hover:bg-astro-primary/80 group-hover:scale-105'
                      }`}>
                        <div className="w-0.5 h-0.5 bg-white rounded-full my-0.5" />
                        <div className="w-0.5 h-0.5 bg-white rounded-full my-0.5" />
                        <div className="w-0.5 h-0.5 bg-white rounded-full my-0.5" />
                      </div>
                    </div>
                  )}

                  {/* Right Column: Viewport-Locked Independently Scrollable Cockpit + House Inspector */}
                  <div
                    ref={rightPanelRef}
                    className="w-full min-w-0 lg:h-[calc(100vh-140px)] lg:max-h-[calc(100vh-140px)] lg:overflow-y-auto pr-1.5 custom-scrollbar shrink-0"
                    style={{ width: isDesktop ? `calc(${100 - splitPercent}% - 14px)` : '100%' }}
                  >
                    <UnifiedPredictiveCockpit
                      kundali={kundali}
                      chartData={currentChartData}
                      selectedHouseNumber={selectedHouseNumber}
                      onSelectHouse={handleSelectHouse}
                      selectedPlanet={selectedPlanet}
                      onSelectPlanet={handleSelectPlanet}
                      lang={lang}
                      layoutMode="dock"
                      showFullHouseInspector={true}
                      splitPercent={splitPercent}
                    />
                  </div>
                </div>
              );
            }

            if (layoutMode === 'top') {
              return (
                <div className="w-full space-y-5">
                  {/* Sticky Top Chart Banner (offset below JatakaProfileHeader with lower z-index) */}
                  <div className="sticky top-[42px] sm:top-[46px] z-10 bg-astro-bg/95 backdrop-blur-md pt-1 pb-3 border-b border-astro-border shadow-xs">
                    <div className="max-w-xl mx-auto flex flex-col items-center">
                      {renderChartCard(true, 420)}
                    </div>
                  </div>
                  {/* Scrollable lower cockpit + House Inspector */}
                  <div className="w-full max-w-5xl mx-auto space-y-5 pt-2">
                    <UnifiedPredictiveCockpit
                      kundali={kundali}
                      chartData={currentChartData}
                      selectedHouseNumber={selectedHouseNumber}
                      onSelectHouse={handleSelectHouse}
                      selectedPlanet={selectedPlanet}
                      onSelectPlanet={handleSelectPlanet}
                      lang={lang}
                      layoutMode="top"
                      showFullHouseInspector={true}
                    />
                  </div>
                </div>
              );
            }

            // Mode: 'wrap' (Adaptive Flow: Left Chart, Right Timing Cockpit, then 100% Full-Width stretch below)
            return (
              <div className="w-full space-y-6">
                <div
                  ref={splitContainerRef}
                  className="w-full flex flex-col lg:flex-row items-start gap-0 lg:gap-2 relative"
                >
                  <div
                    ref={leftPanelRef}
                    className="w-full lg:sticky lg:top-[50px] z-10 flex flex-col items-center shrink-0"
                    style={{ width: isDesktop ? `${splitPercent}%` : '100%' }}
                  >
                    {renderChartCard(false)}
                  </div>

                  {isDesktop && (
                    <div
                      role="separator"
                      tabIndex={0}
                      aria-orientation="vertical"
                      onMouseDown={() => setIsDraggingSplit(true)}
                      onTouchStart={() => setIsDraggingSplit(true)}
                      onDoubleClick={handleResetSplit}
                      className="flex items-center justify-center w-3.5 self-stretch cursor-col-resize group select-none py-6 shrink-0 transition-all z-10 -mx-1"
                      title={activeLang === 'hi' ? 'खींचकर आकार बदलें (डबल-क्लिक: ५०:५० रीसेट)' : 'Drag to resize panels (Double-click: 50:50 reset)'}
                    >
                      <div className={`w-1.5 h-16 rounded-full flex flex-col items-center justify-center transition-all ${
                        isDraggingSplit ? 'bg-astro-primary scale-110 shadow-xs' : 'bg-[#D5CEBF] group-hover:bg-astro-primary/80 group-hover:scale-105'
                      }`}>
                        <div className="w-0.5 h-0.5 bg-white rounded-full my-0.5" />
                        <div className="w-0.5 h-0.5 bg-white rounded-full my-0.5" />
                        <div className="w-0.5 h-0.5 bg-white rounded-full my-0.5" />
                      </div>
                    </div>
                  )}

                  <div
                    ref={rightPanelRef}
                    className="w-full min-w-0 overflow-x-hidden shrink-0"
                    style={{ width: isDesktop ? `calc(${100 - splitPercent}% - 14px)` : '100%' }}
                  >
                    <UnifiedPredictiveCockpit
                      kundali={kundali}
                      chartData={currentChartData}
                      selectedHouseNumber={selectedHouseNumber}
                      onSelectHouse={handleSelectHouse}
                      selectedPlanet={selectedPlanet}
                      onSelectPlanet={handleSelectPlanet}
                      lang={lang}
                      layoutMode="wrap"
                      showFullHouseInspector={false}
                      splitPercent={splitPercent}
                    />
                    <div className="w-full mt-4">
                      <HouseInspectorView
                        selectedHouseNumber={selectedHouseNumber}
                        onSelectHouse={handleSelectHouse}
                        selectedPlanet={selectedPlanet}
                        onSelectPlanet={handleSelectPlanet}
                        chartData={currentChartData}
                        kundali={kundali}
                        lang={lang}
                        layoutMode="split-top"
                      />
                    </div>
                  </div>
                </div>
                {/* Once left panel ends, remainder stretches 100% full screen width */}
                <div className="w-full">
                  <HouseInspectorView
                    selectedHouseNumber={selectedHouseNumber}
                    onSelectHouse={handleSelectHouse}
                    selectedPlanet={selectedPlanet}
                    onSelectPlanet={handleSelectPlanet}
                    chartData={currentChartData}
                    kundali={kundali}
                    lang={lang}
                    layoutMode="split-bottom"
                  />
                </div>
              </div>
            );
          })()
        ) : (
          <div className="bg-[#FDFCF9] border border-[#E2DED5] rounded-2xl p-6 shadow-xs">
            {selectedDivisionalCode === 'Gochar' && <GocharView kundali={kundali} lang={lang} />}
            {selectedDivisionalCode === 'Dasha' && <DashaTimeline kundali={kundali} lang={lang} />}
            {selectedDivisionalCode === 'Ashtakvarga' && <AshtakvargaView kundali={kundali} />}
            {selectedDivisionalCode === 'HouseStrength' && (
              <HouseBreakdownView 
                kundali={kundali} 
                lang={lang} 
                onNavigateToHouse={(hNum) => {
                  setSelectedDivisionalCode('D1');
                  setSelectedHouseNumber(hNum);
                  setViewMode('single');
                }}
              />
            )}
            {selectedDivisionalCode === 'Shadbala' && <ShadbalaView kundali={kundali} />}
            {selectedDivisionalCode === 'Navtara' && <NavtaraView kundali={kundali} lang={lang} />}
            {selectedDivisionalCode === 'Muhurta' && <MuhurtaAdvisor kundali={kundali} lang={lang} />}
            {(selectedDivisionalCode === 'FullPanchang' || selectedDivisionalCode === 'Panchang') && (
              <FullPanchangView kundali={kundali} lang={lang} />
            )}
            {selectedDivisionalCode === 'Karka' && <KarkaView kundali={kundali} />}
            {selectedDivisionalCode === 'PublicationDossier' && (
              <PublicationDossierView kundali={kundali} lang={lang} chartStyle={chartStyle} />
            )}
            {selectedDivisionalCode === 'ShubhaEssentials' && (
              <RemediesView
                kundali={kundali}
                lang={lang}
                initialTab="overview"
              />
            )}
            {(selectedDivisionalCode === 'Gemstones' || selectedDivisionalCode === 'Remedies') && (
              <RemediesView
                kundali={kundali}
                lang={lang}
                initialTab={selectedDivisionalCode === 'Gemstones' ? 'gemstones' : 'remedies'}
              />
            )}
            {selectedDivisionalCode === 'AstrologerDossier' && <AstrologerDiagnosticDossier kundali={kundali} lang={lang} />}
          </div>
        )
      )}

      {/* DUAL VIEW: Comparative Side-by-Side View */}
      {viewMode === 'dual' && (
        <DualViewLayout
          kundali={kundali}
          chartStyle={chartStyle}
          lang={lang}
          showDegrees={showDegrees}
          onSelectPlanet={handleSelectPlanet}
          onSelectHouse={handleSelectHouse}
        />
      )}

      {/* QUAD VIEW: 4 Core Kundalis Side-by-Side on Single Page */}
      {viewMode === 'quad' && (
        <div className="space-y-4">
          <div className="bg-[#FDFCF9] border border-[#E2DED5] rounded-2xl p-4 flex items-center justify-between shadow-xs">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-astro-secondary" />
              <h3 className="text-sm font-bold text-[#263633]">
                Customizable 4-Quadrant Overview
              </h3>
            </div>
            <span className="text-xs text-astro-secondary font-serif font-semibold hidden sm:inline">
              Style: {chartStyle.toUpperCase()} Indian
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {quadSelections.map((selection, index) => {
              const chartData = (kundali.charts as any)[selection] || kundali.charts.D1;

              return (
                <div
                  key={index}
                  className="bg-[#FDFCF9] border border-[#E2DED5] rounded-2xl p-5 shadow-xs flex flex-col items-center hover:border-astro-secondary/40 transition-all group"
                >
                  <div className="w-full mb-3 border-b border-[#E2DED5] pb-2.5 flex items-center justify-between gap-2">
                    <select
                      value={selection}
                      onChange={(e) => {
                        const newSelections = [...quadSelections];
                        newSelections[index] = e.target.value;
                        setQuadSelections(newSelections);
                      }}
                      className="text-sm font-bold text-astro-secondary bg-astro-card p-2 rounded-lg border border-[#E2DED5] flex-1 cursor-pointer"
                    >
                      {quadOptions.map((d) => (
                        <option key={d.code} value={d.code}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => {
                        setExpandedSelection(selection);
                      }}
                      className="p-2 hover:bg-[#E2DED5] rounded-lg transition-colors cursor-pointer"
                      title="Expand this view"
                    >
                      <Maximize2 className="w-4 h-4 text-astro-secondary" />
                    </button>
                  </div>

                  <ResponsiveChartContainer maxDimension={520} minDimension={180} className="my-1">
                    {() => (
                      <>
                        {divisionalList.some(d => d.code === selection) ? (
                          <>
                            {chartStyle === 'north' && (
                              <NorthIndianSvgChart
                                chartData={chartData}
                                planets={chartData.planets}
                                ashtakvarga={kundali.ashtakvarga}
                                selectedHouseNumber={selectedHouseNumber}
                                onSelectHouse={handleSelectHouse}
                                onSelectPlanet={handleSelectPlanet}
                                lang={lang}
                                showDegrees={showDegrees}
                                antiOverlap={antiOverlap}
                              />
                            )}
                            {chartStyle === 'south' && (
                              <SouthIndianSvgChart
                                chartData={chartData}
                                planets={chartData.planets}
                                ashtakvarga={kundali.ashtakvarga}
                                selectedHouseNumber={selectedHouseNumber}
                                onSelectHouse={handleSelectHouse}
                                onSelectPlanet={handleSelectPlanet}
                                lang={lang}
                                showDegrees={showDegrees}
                                antiOverlap={antiOverlap}
                              />
                            )}
                            {chartStyle === 'east' && (
                              <EastIndianSvgChart
                                chartData={chartData}
                                planets={chartData.planets}
                                ashtakvarga={kundali.ashtakvarga}
                                selectedHouseNumber={selectedHouseNumber}
                                onSelectHouse={handleSelectHouse}
                                onSelectPlanet={handleSelectPlanet}
                                lang={lang}
                                showDegrees={showDegrees}
                                antiOverlap={antiOverlap}
                              />
                            )}
                          </>
                        ) : (
                          <div className="w-full h-full overflow-y-auto bg-astro-card rounded-xl p-2">
                            {selection === 'Gochar' && <GocharView kundali={kundali} lang={lang} />}
                            {selection === 'Dasha' && <DashaTimeline kundali={kundali} lang={lang} />}
                            {selection === 'Ashtakvarga' && <AshtakvargaView kundali={kundali} lang={lang} />}
                            {selection === 'HouseStrength' && (
                              <HouseBreakdownView 
                                kundali={kundali} 
                                lang={lang} 
                                onNavigateToHouse={(hNum) => {
                                  setSelectedDivisionalCode('D1');
                                  setSelectedHouseNumber(hNum);
                                  setViewMode('single');
                                }}
                              />
                            )}
                            {selection === 'Shadbala' && <ShadbalaView kundali={kundali} lang={lang} />}
                            {selection === 'Navtara' && <NavtaraView kundali={kundali} lang={lang} />}
                            {selection === 'Muhurta' && <MuhurtaAdvisor kundali={kundali} lang={lang} />}
                            {(selection === 'FullPanchang' || selection === 'Panchang') && (
                              <FullPanchangView kundali={kundali} lang={lang} />
                            )}
                            {selection === 'Karka' && <KarkaView kundali={kundali} />}
                            {selection === 'Remedies' && (
                              <UnifiedRemedialEngineView
                                kundali={kundali}
                                chartData={currentChartData}
                                selectedHouseNumber={selectedHouseNumber}
                                lang={lang}
                              />
                            )}
                            {selection === 'AstrologerDossier' && <AstrologerDiagnosticDossier kundali={kundali} lang={lang} />}
                          </div>
                        )}
                      </>
                    )}
                  </ResponsiveChartContainer>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* EXPANDED SINGLE VIEW FROM QUAD OVERVIEW */}
      {viewMode === 'quad' && expandedSelection && (
        <div className="bg-astro-bg border-2 border-astro-primary/40 rounded-2xl p-6 shadow-md space-y-6">
          <div className="flex items-center justify-between border-b border-astro-border pb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setExpandedSelection(null)}
                className="px-4 py-2 rounded-xl bg-astro-primary text-white text-xs font-bold hover:bg-astro-secondary transition-colors flex items-center gap-2 shadow-sm"
              >
                <span>← {lang === 'hi' ? '4 चार्ट दृश्य पर वापस' : 'Back to 4 Chart View'}</span>
              </button>
              <h3 className="font-bold text-astro-primary font-serif text-lg">
                {lang === 'hi' ? 'विस्तृत दृश्य:' : 'Expanded View:'} {quadOptions.find(o => o.code === expandedSelection)?.label || expandedSelection}
              </h3>
            </div>
            <span className="text-xs text-astro-primary font-serif font-semibold hidden sm:inline">
              {lang === 'hi' ? 'शैली:' : 'Style:'} {chartStyle.toUpperCase()} {lang === 'hi' ? 'भारतीय' : 'Indian'}
            </span>
          </div>

          <div className="w-full">
            {divisionalList.some(d => d.code === expandedSelection) ? (
              <ResponsiveChartContainer maxDimension={760} minDimension={240} className="my-2">
                {() => (
                  <>
                    {chartStyle === 'north' && (
                      <NorthIndianSvgChart
                        chartData={(kundali.charts as any)[expandedSelection] || kundali.charts.D1}
                        planets={((kundali.charts as any)[expandedSelection] || kundali.charts.D1).planets}
                        ashtakvarga={kundali.ashtakvarga}
                        selectedHouseNumber={selectedHouseNumber}
                        onSelectHouse={handleSelectHouse}
                        onSelectPlanet={handleSelectPlanet}
                        lang={lang}
                        showDegrees={showDegrees}
                        antiOverlap={antiOverlap}
                      />
                    )}
                    {chartStyle === 'south' && (
                      <SouthIndianSvgChart
                        chartData={(kundali.charts as any)[expandedSelection] || kundali.charts.D1}
                        planets={((kundali.charts as any)[expandedSelection] || kundali.charts.D1).planets}
                        ashtakvarga={kundali.ashtakvarga}
                        selectedHouseNumber={selectedHouseNumber}
                        onSelectHouse={handleSelectHouse}
                        onSelectPlanet={handleSelectPlanet}
                        lang={lang}
                        showDegrees={showDegrees}
                        antiOverlap={antiOverlap}
                      />
                    )}
                    {chartStyle === 'east' && (
                      <EastIndianSvgChart
                        chartData={(kundali.charts as any)[expandedSelection] || kundali.charts.D1}
                        planets={((kundali.charts as any)[expandedSelection] || kundali.charts.D1).planets}
                        ashtakvarga={kundali.ashtakvarga}
                        selectedHouseNumber={selectedHouseNumber}
                        onSelectHouse={handleSelectHouse}
                        onSelectPlanet={handleSelectPlanet}
                        lang={lang}
                        showDegrees={showDegrees}
                        antiOverlap={antiOverlap}
                      />
                    )}
                  </>
                )}
              </ResponsiveChartContainer>
            ) : (
              <div className="w-full bg-astro-card rounded-xl p-4">
                {expandedSelection === 'Gochar' && <GocharView kundali={kundali} lang={lang} />}
                {expandedSelection === 'Dasha' && <DashaTimeline kundali={kundali} lang={lang} />}
                {expandedSelection === 'Ashtakvarga' && <AshtakvargaView kundali={kundali} lang={lang} />}
                {expandedSelection === 'HouseStrength' && (
                  <HouseBreakdownView 
                    kundali={kundali} 
                    lang={lang} 
                    onNavigateToHouse={(hNum) => {
                      setSelectedDivisionalCode('D1');
                      setSelectedHouseNumber(hNum);
                      setViewMode('single');
                      setExpandedSelection(null);
                    }}
                  />
                )}
                {expandedSelection === 'Shadbala' && <ShadbalaView kundali={kundali} lang={lang} />}
                {expandedSelection === 'Navtara' && <NavtaraView kundali={kundali} lang={lang} />}
                {expandedSelection === 'Muhurta' && <MuhurtaAdvisor kundali={kundali} lang={lang} />}
                {expandedSelection === 'Karka' && <KarkaView kundali={kundali} lang={lang} />}
                {expandedSelection === 'Remedies' && (
                  <UnifiedRemedialEngineView
                    kundali={kundali}
                    chartData={currentChartData}
                    selectedHouseNumber={selectedHouseNumber}
                    lang={lang}
                  />
                )}
                {expandedSelection === 'AstrologerDossier' && <AstrologerDiagnosticDossier kundali={kundali} lang={lang} />}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Universal Single Chart Analysis below dual/quad layouts */}
      {(viewMode === 'dual' || viewMode === 'quad') && (
        <div className="mt-8 pt-6 border-t border-astro-border w-full min-w-0 max-w-full overflow-hidden">
          <div className="flex items-center gap-2 mb-4 px-2 text-astro-primary">
            <Compass className="w-5 h-5" />
            <h3 className="font-bold font-serif text-lg">
              {lang === 'hi' ? `भाव ${selectedHouseNumber} का सार्वभौमिक विश्लेषण (Universal Analysis)` : `Universal Analysis for House ${selectedHouseNumber}`}
            </h3>
          </div>
          <div className="w-full min-w-0 max-w-full overflow-hidden">
            <HouseInspectorView
              selectedHouseNumber={selectedHouseNumber}
              onSelectHouse={handleSelectHouse}
              selectedPlanet={selectedPlanet}
              onSelectPlanet={handleSelectPlanet}
              chartData={kundali.charts.D1}
              kundali={kundali}
              lang={lang}
            />
          </div>
        </div>
      )}
    </div>
  );
};



export const ChartLegend: React.FC<{ lang?: Language }> = ({ lang = 'en' }) => {
  const isHi = lang === 'hi';
  return (
    <div className="w-full flex flex-wrap items-center justify-center gap-2.5 sm:gap-4 text-[11px] text-[#52635F] pt-3 border-t border-[#E2DED5] mt-3">
      {/* Planetary Dignity Indicators with High-Contrast Badges */}
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 font-bold shadow-xs">
        <span className="w-2 h-2 rounded-full bg-emerald-600" />
        <span>{isHi ? 'उच्च (↑)' : 'Exalted (↑)'}</span>
      </span>
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 border border-rose-300 font-bold shadow-xs">
        <span className="w-2 h-2 rounded-full bg-rose-600" />
        <span>{isHi ? 'नीच (↓)' : 'Debilitated (↓)'}</span>
      </span>
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-astro-teal-tint text-astro-secondary border border-[#E2DED5] font-semibold shadow-xs">
        <span className="w-2 h-2 rounded-full bg-astro-secondary" />
        <span>{isHi ? 'वक्री (व)' : 'Retrograde (R)'}</span>
      </span>

      {/* Lagna / Ascendant Indicator */}
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-astro-card border border-[#E2DED5]">
        <span className="font-sans font-bold text-astro-secondary text-xs">
          {isHi ? 'लग्न (ASC)' : 'ASC (Lagna)'}
        </span>
        <span className="font-sans text-[#263633]">{isHi ? 'प्रथम भाव लग्न' : 'Ascendant / House 1'}</span>
      </span>

      {/* Distinction: Rashi Number vs SAV Points */}
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-astro-card border border-[#E2DED5]">
        <span className="font-serif font-bold text-astro-secondary text-xs">
          1-12
        </span>
        <span className="font-sans text-[#263633]">{isHi ? 'राशि क्रमांक (Sign #)' : 'Rashi (Sign #)'}</span>
      </span>
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-astro-card border border-[#E2DED5]">
        <span className="font-sans font-bold text-astro-accent text-xs">
          SAV 28
        </span>
        <span className="font-sans text-[#263633]">{isHi ? 'सर्वाष्टकवर्ग (SAV अंक)' : 'Sarvashtakvarga (SAV)'}</span>
      </span>
    </div>
  );
};

interface SvgChartProps {
  chartData?: KundaliChartData;
  chart?: KundaliChartData;
  planets?: PlanetPosition[];
  ashtakvarga?: AshtakvargaData;
  selectedHouseNumber?: number;
  onSelectHouse?: (h: number) => void;
  onSelectPlanet?: (p: PlanetPosition) => void;
  lang?: Language;
  showDegrees?: boolean;
  antiOverlap?: boolean;
  size?: number;
}

export const NorthIndianSvgChart: React.FC<SvgChartProps> = ({
  chartData: propChartData,
  chart,
  planets: propPlanets,
  ashtakvarga,
  selectedHouseNumber,
  onSelectHouse,
  onSelectPlanet,
  lang = 'en',
  showDegrees = false,
  antiOverlap = true
}) => {
  const chartData = propChartData || chart;
  const planets = propPlanets || chartData?.planets || [];
  const activeLang: Language = (lang as Language) || 'en';

  const resolvedChart = useMemo(() => {
    return resolveChartLayout({
      chartStyle: 'north',
      chartData,
      planets,
      ashtakvarga,
      showDegrees,
      lang: activeLang,
      antiOverlap,
      selectedHouseNumber
    });
  }, [chartData, planets, ashtakvarga, showDegrees, activeLang, antiOverlap, selectedHouseNumber]);

  return (
    <svg
      viewBox="0 0 400 400"
      className="w-full h-full max-w-full max-h-full object-contain select-none"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <clipPath id="ni-clip">
          <rect x="10" y="10" width="380" height="380" rx="12" />
        </clipPath>
      </defs>

      {/* Outer Border Box */}
      <rect x="10" y="10" width="380" height="380" fill="#FDFCF9" rx="12" />

      <g clipPath="url(#ni-clip)">
        {/* Clickable House Polygons with Highlight */}
        {resolvedChart.houses.map((h) => {
          const isSelected = selectedHouseNumber === h.houseNumber;
          return (
            <polygon
              key={`poly-${h.houseNumber}`}
              points={h.polygonPoints}
              fill={isSelected ? '#1F4F4E' : 'transparent'}
              fillOpacity={isSelected ? 0.12 : 0}
              stroke={isSelected ? '#1F4F4E' : 'none'}
              strokeWidth={isSelected ? 1.5 : 0}
              onClick={() => onSelectHouse?.(h.houseNumber)}
              className="cursor-pointer transition-colors duration-150"
            />
          );
        })}

        {/* Main Diagonals - Intersection Lines */}
        <line x1="10" y1="10" x2="390" y2="390" stroke="#1F4F4E" strokeWidth="1" opacity="0.4" pointerEvents="none" />
        <line x1="390" y1="10" x2="10" y2="390" stroke="#1F4F4E" strokeWidth="1" opacity="0.4" pointerEvents="none" />

        {/* Inner Diamond */}
        <polygon points="200,10 390,200 200,390 10,200" fill="none" stroke="#1F4F4E" strokeWidth="1.5" pointerEvents="none" />
      </g>

      <rect x="10" y="10" width="380" height="380" fill="none" stroke="#1F4F4E" strokeWidth="1.5" rx="12" pointerEvents="none" />

      {/* Render Houses 1 to 12: Rashi Numbers, Lagna Badge, and Ashtakvarga Points */}
      {resolvedChart.houses.map((h) => {
        const isKendra = [1, 4, 7, 10].includes(h.houseNumber);
        const isSelected = selectedHouseNumber === h.houseNumber;

        return (
          <g
            key={`h-meta-${h.houseNumber}`}
            onClick={() => onSelectHouse?.(h.houseNumber)}
            className="cursor-pointer"
          >
            {/* Rashi Sign Number (Zodiac Sign 1-12) - In dedicated semantic corner/edge zone */}
            <text
              x={h.rashiAnchor.x}
              y={h.rashiAnchor.y}
              textAnchor="middle"
              dominantBaseline="central"
              fill={isSelected ? '#1F4F4E' : isKendra ? '#1F4F4E' : '#263633'}
              fontSize={isSelected ? '18' : isKendra ? '17' : '15'}
              fontWeight="bold"
              fontFamily="serif"
              data-collision-id={`h${h.houseNumber}-sign`}
              data-house={h.houseNumber}
              data-role="sign-num"
            >
              {h.rashiAnchor.text}
            </text>

            {/* Dedicated Real-Time Isolated Lagna Badge for House 1 */}
            {h.lagnaAnchor && (
              <g
                data-collision-id="h1-lagna-badge"
                data-house="1"
                data-role="lagna-badge"
                transform={`translate(${h.lagnaAnchor.x}, ${h.lagnaAnchor.y})`}
                onClick={(e) => {
                  e.stopPropagation();
                  const ascPlanet = planets.find((p) => p.name === 'Ascendant');
                  if (ascPlanet) onSelectPlanet?.(ascPlanet);
                  onSelectHouse?.(1);
                }}
                className="cursor-pointer hover:opacity-85 transition-opacity"
              >
                {/* Visual isolation pill backdrop */}
                <rect
                  x={-h.lagnaAnchor.bounds.width / 2}
                  y={-h.lagnaAnchor.bounds.height / 2}
                  width={h.lagnaAnchor.bounds.width}
                  height={h.lagnaAnchor.bounds.height}
                  rx="5"
                  fill="#FDFCF9"
                  fillOpacity="0.95"
                  stroke="#1F4F4E"
                  strokeWidth="1.2"
                  strokeOpacity="0.7"
                />
                <text
                  x="0"
                  y="0"
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="#1F4F4E"
                  fontSize="11"
                  fontWeight="900"
                  fontFamily="sans-serif"
                  letterSpacing="0.4"
                >
                  {h.lagnaAnchor.text}
                </text>
              </g>
            )}

            {/* Ashtakvarga (SAV) Points Display - Micro Pill Badge */}
            {h.savAnchor && (
              <g
                data-collision-id={`h${h.houseNumber}-sav`}
                data-house={h.houseNumber}
                data-role="sav"
                transform={`translate(${h.savAnchor.x}, ${h.savAnchor.y})`}
              >
                <rect
                  x="-12"
                  y="-6.5"
                  width="24"
                  height="13"
                  rx="3"
                  fill="#FEF3C7"
                  fillOpacity="0.95"
                  stroke="#D97706"
                  strokeWidth="0.8"
                />
                <text
                  x="0"
                  y="0.5"
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="#92400E"
                  fontSize="8.5"
                  fontWeight="bold"
                  fontFamily="sans-serif"
                >
                  {h.savAnchor.points}
                </text>
              </g>
            )}
          </g>
        );
      })}

      {/* Render Occupant Planets in Houses */}
      {resolvedChart.houses.map((h) => {
        if (h.planets.length === 0) return null;
        return (
          <g key={`h-planets-${h.houseNumber}`}>
            {h.planets.map((p) => {
              return (
                <g
                  key={p.name}
                  data-collision-id={`h${h.houseNumber}-p-${p.name}`}
                  data-house={h.houseNumber}
                  data-role="planet"
                  transform={`translate(${p.x}, ${p.y})`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectPlanet?.(p.planet);
                    onSelectHouse?.(h.houseNumber);
                  }}
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <text
                    x="0"
                    y="3.5"
                    textAnchor="middle"
                    fill={p.fillColor}
                    fontSize={p.fontSize}
                    fontWeight="bold"
                    fontFamily="sans-serif"
                    className="drop-shadow-sm"
                  >
                    {p.labelText}
                    {p.dignityArrow === '↑' && (
                      <tspan fill="#059669" fontWeight="900" fontSize={`${p.fontSize * 1.4}`} dy="-1">↑</tspan>
                    )}
                    {p.dignityArrow === '↓' && (
                      <tspan fill="#dc2626" fontWeight="900" fontSize={`${p.fontSize * 1.4}`} dy="1">↓</tspan>
                    )}
                  </text>
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
};

/* SOUTH INDIAN SVG CHART COMPONENT */
export const SouthIndianSvgChart: React.FC<SvgChartProps> = ({
  chartData: propChartData,
  chart,
  planets: propPlanets,
  ashtakvarga,
  selectedHouseNumber,
  onSelectHouse,
  onSelectPlanet,
  lang = 'en',
  showDegrees = false,
  antiOverlap = true
}) => {
  const chartData = propChartData || chart;
  const planets = propPlanets || chartData?.planets || [];
  const activeLang: Language = (lang as Language) || 'en';

  const resolvedChart = useMemo(() => {
    return resolveChartLayout({
      chartStyle: 'south',
      chartData,
      planets,
      ashtakvarga,
      showDegrees,
      lang: activeLang,
      antiOverlap,
      selectedHouseNumber
    });
  }, [chartData, planets, ashtakvarga, showDegrees, activeLang, antiOverlap, selectedHouseNumber]);

  return (
    <svg
      viewBox="0 0 400 400"
      className="w-full h-full max-w-full max-h-full object-contain select-none"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <clipPath id="si-clip">
          <rect x="5" y="5" width="390" height="390" rx="12" />
        </clipPath>
      </defs>

      <rect x="5" y="5" width="390" height="390" fill="#FDFCF9" rx="12" />

      <g clipPath="url(#si-clip)">
        {/* 4x4 Grid Lines */}
        <line x1="5" y1="102.5" x2="395" y2="102.5" stroke="#6F8876" strokeWidth="1.2" opacity="0.75" />
        <line x1="5" y1="200" x2="395" y2="200" stroke="#6F8876" strokeWidth="1.2" opacity="0.75" />
        <line x1="5" y1="297.5" x2="395" y2="297.5" stroke="#6F8876" strokeWidth="1.2" opacity="0.75" />
        <line x1="102.5" y1="5" x2="102.5" y2="395" stroke="#6F8876" strokeWidth="1.2" opacity="0.75" />
        <line x1="200" y1="5" x2="200" y2="395" stroke="#6F8876" strokeWidth="1.2" opacity="0.75" />
        <line x1="297.5" y1="5" x2="297.5" y2="395" stroke="#6F8876" strokeWidth="1.2" opacity="0.75" />

        {/* Middle Center Box Fill */}
        <rect x="102.5" y="102.5" width="195" height="195" fill="#F7F3EA" />
        <text x="200" y="195" textAnchor="middle" fill="#1F4F4E" fontSize="13" fontWeight="bold" fontFamily="serif">
          {chartData?.title || 'Chart'}
        </text>

        {/* Render 12 Sign Boxes */}
        {resolvedChart.houses.map((h) => {
          const isSelected = selectedHouseNumber === h.houseNumber;
          const box = h.box!;

          return (
            <g
              key={h.signIndex}
              onClick={() => onSelectHouse?.(h.houseNumber)}
              className="cursor-pointer"
            >
              {/* Selected House Highlight */}
              {isSelected && (
                <rect
                  x={box.x}
                  y={box.y}
                  width={box.width}
                  height={box.height}
                  fill="#1F4F4E"
                  fillOpacity="0.1"
                  stroke="#1F4F4E"
                  strokeWidth="1.5"
                />
              )}

              {/* Rashi Sign Header & House Number */}
              <text
                x={h.rashiAnchor.x}
                y={h.rashiAnchor.y}
                fill="#1F4F4E"
                fontSize={h.rashiAnchor.fontSize}
                fontWeight="bold"
                data-collision-id={`si-sign-${h.signIndex}`}
                data-house={h.signIndex}
                data-role="sign-num"
              >
                {h.rashiAnchor.text}
              </text>
              {h.houseNumAnchor && (
                <text
                  x={h.houseNumAnchor.x}
                  y={h.houseNumAnchor.y}
                  fill="#52635F"
                  fontSize={h.houseNumAnchor.fontSize}
                  fontWeight="semibold"
                  data-collision-id={`si-hnum-${h.signIndex}`}
                  data-house={h.signIndex}
                  data-role="house-num"
                >
                  {h.houseNumAnchor.text}
                </text>
              )}

              {/* Lagna Badge */}
              {h.lagnaAnchor && (
                <g
                  transform={`translate(${h.lagnaAnchor.x}, ${h.lagnaAnchor.y})`}
                  onClick={(e) => {
                    e.stopPropagation();
                    const ascPlanet = planets.find((p) => p.name === 'Ascendant');
                    if (ascPlanet) onSelectPlanet?.(ascPlanet);
                    onSelectHouse?.(1);
                  }}
                  className="cursor-pointer hover:opacity-85"
                  data-collision-id={`si-lagna-${h.signIndex}`}
                  data-house={h.signIndex}
                  data-role="lagna-badge"
                >
                  <rect
                    x={-h.lagnaAnchor.bounds.width / 2}
                    y={-h.lagnaAnchor.bounds.height / 2}
                    width={h.lagnaAnchor.bounds.width}
                    height={h.lagnaAnchor.bounds.height}
                    rx="3"
                    fill="#1F4F4E"
                    fillOpacity="0.12"
                  />
                  <text
                    x="0"
                    y="0"
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="#1F4F4E"
                    fontSize="7.5"
                    fontWeight="900"
                  >
                    {h.lagnaAnchor.text}
                  </text>
                </g>
              )}

              {/* Ashtakvarga (SAV) Points - Micro Pill Badge */}
              {h.savAnchor && (
                <g
                  data-collision-id={`si-sav-${h.signIndex}`}
                  data-house={h.signIndex}
                  data-role="sav"
                  transform={`translate(${h.savAnchor.x}, ${h.savAnchor.y})`}
                >
                  <rect
                    x="-10"
                    y="-5.5"
                    width="20"
                    height="11"
                    rx="2.5"
                    fill="#FEF3C7"
                    stroke="#D97706"
                    strokeWidth="0.8"
                  />
                  <text
                    x="0"
                    y="0.5"
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="#92400E"
                    fontSize="7.5"
                    fontWeight="bold"
                    fontFamily="sans-serif"
                  >
                    {h.savAnchor.points}
                  </text>
                </g>
              )}

              {/* Occupant Planets in Sign */}
              {h.planets.map((p) => {
                return (
                  <g
                    key={p.name}
                    data-collision-id={`si-p-${h.signIndex}-${p.name}`}
                    data-house={h.signIndex}
                    data-role="planet"
                    transform={`translate(${p.x}, ${p.y})`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectPlanet?.(p.planet);
                      onSelectHouse?.(h.houseNumber);
                    }}
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                  >
                    <text x="0" y="3" textAnchor="middle" fill={p.fillColor} fontSize={p.fontSize} fontWeight="bold">
                      {p.labelText}
                      {p.dignityArrow === '↑' && (
                        <tspan fill="#059669" fontWeight="900" fontSize={`${p.fontSize * 1.4}`} dy="-1">↑</tspan>
                      )}
                      {p.dignityArrow === '↓' && (
                        <tspan fill="#dc2626" fontWeight="900" fontSize={`${p.fontSize * 1.4}`} dy="1">↓</tspan>
                      )}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}
      </g>
      <rect x="5" y="5" width="390" height="390" fill="none" stroke="#1F4F4E" strokeWidth="2" rx="12" pointerEvents="none" />
    </svg>
  );
};

export const EastIndianSvgChart: React.FC<SvgChartProps> = ({
  chartData: propChartData,
  chart,
  planets: propPlanets,
  ashtakvarga,
  selectedHouseNumber,
  onSelectHouse,
  onSelectPlanet,
  lang = 'en',
  showDegrees = false,
  antiOverlap = true
}) => {
  const chartData = propChartData || chart;
  const planets = propPlanets || chartData?.planets || [];
  const activeLang: Language = (lang as Language) || 'en';

  const resolvedChart = useMemo(() => {
    return resolveChartLayout({
      chartStyle: 'east',
      chartData,
      planets,
      ashtakvarga,
      showDegrees,
      lang: activeLang,
      antiOverlap,
      selectedHouseNumber
    });
  }, [chartData, planets, ashtakvarga, showDegrees, activeLang, antiOverlap, selectedHouseNumber]);

  return (
    <svg
      viewBox="0 0 400 400"
      className="w-full h-full max-w-full max-h-full object-contain select-none"
      preserveAspectRatio="xMidYMid meet"
    >
      <rect x="10" y="10" width="380" height="380" fill="#FDFCF9" stroke="#1F4F4E" strokeWidth="2" rx="12" />
      <line x1="10" y1="200" x2="390" y2="200" stroke="#E2DED5" strokeWidth="1.5" />
      <line x1="200" y1="10" x2="200" y2="390" stroke="#E2DED5" strokeWidth="1.5" />
      <polygon points="200,10 390,200 200,390 10,200" fill="none" stroke="#1F4F4E" strokeWidth="1.5" />

      <text x="200" y="195" textAnchor="middle" fill="#1F4F4E" fontSize="13" fontWeight="bold" fontFamily="serif">
        {chartData?.title || 'Chart'}
      </text>

      {resolvedChart.houses.map((h) => {
        const isSelected = selectedHouseNumber === h.houseNumber;
        const baseX = h.rashiAnchor.x;
        const baseY = h.rashiAnchor.y + 12;

        return (
          <g
            key={`east-h-${h.houseNumber}`}
            onClick={() => onSelectHouse?.(h.houseNumber)}
            className="cursor-pointer"
          >
            {/* House indicator */}
            <circle
              cx={baseX}
              cy={baseY}
              r="24"
              fill={isSelected ? '#1F4F4E' : 'transparent'}
              fillOpacity={isSelected ? 0.12 : 0}
            />

            {/* Rashi Sign Indicator & Lagna Badge */}
            <text
              x={h.rashiAnchor.x}
              y={h.rashiAnchor.y}
              textAnchor="middle"
              fill="#1F4F4E"
              fontSize={h.rashiAnchor.fontSize}
              fontWeight="bold"
              fontFamily="serif"
              data-collision-id={`ei-sign-${h.houseNumber}`}
              data-house={h.houseNumber}
              data-role="sign-num"
            >
              R{h.rashiAnchor.text} [H{h.houseNumber}]{h.houseNumber === 1 ? ` ${activeLang === 'hi' ? 'लग्न' : 'ASC'}` : ''}
            </text>

            {h.planets.map((p) => {
              return (
                <g
                  key={p.name}
                  data-collision-id={`ei-p-${h.houseNumber}-${p.name}`}
                  data-house={h.houseNumber}
                  data-role="planet"
                  transform={`translate(${p.x}, ${p.y})`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectPlanet?.(p.planet);
                    onSelectHouse?.(h.houseNumber);
                  }}
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <text
                    x="0"
                    y="3.5"
                    textAnchor="middle"
                    fill={p.fillColor}
                    fontSize={p.fontSize}
                    fontWeight="bold"
                    fontFamily="sans-serif"
                    className="drop-shadow-sm"
                  >
                    {p.labelText}
                    {p.dignityArrow === '↑' && (
                      <tspan fill="#059669" fontWeight="900" fontSize={`${p.fontSize * 1.4}`} dy="-1">↑</tspan>
                    )}
                    {p.dignityArrow === '↓' && (
                      <tspan fill="#dc2626" fontWeight="900" fontSize={`${p.fontSize * 1.4}`} dy="1">↓</tspan>
                    )}
                  </text>
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
};
